<template>
  <AppNavbar />
  <div class="note-wrapper">
    <div class="note-paper">
      <header class="note-header">
        <div class="note-header__brand">
          <span class="note-badge">Documento auxiliar</span>
          <h1 class="note-title">Nota de Remisión</h1>
          <p class="note-description">
            Completa los datos de traslado tal como aparecen en el comprobante físico para asegurar la trazabilidad.
          </p>
        </div>
        <div class="note-header__meta">
          <div class="meta-grid">
            <div class="meta-field">
              <label for="timbrado" class="form-label">Timbrado</label>
              <input
                id="timbrado"
                type="text"
                ref="timbradoInput"
                v-model="notaData.timbrado"
                class="form-control"
                placeholder="Número de timbrado"
                :readonly="readOnly"
                @keydown.enter.prevent="focusRef('fechaEmisionInput')"
              />
            </div>
            <div class="meta-field">
              <label for="fecha_emision" class="form-label">Fecha de Emisión</label>
              <!-- Editable: usar date picker; Solo lectura: mostrar dd/mm/yyyy -->
              <input
                v-if="!readOnly"
                id="fecha_emision"
                type="text"
                ref="fechaEmisionInput"
                class="form-control"
                :placeholder="DATE_PLACEHOLDER"
                inputmode="numeric"
                maxlength="10"
                @input="onFechaEmisionTyping"
                @keydown.enter.prevent="focusRef('nroNotaInput')"
              />
              <input
                v-else
                id="fecha_emision"
                type="text"
                :value="formatDateDisplay(notaData.fecha_emision)"
                class="form-control"
                readonly
              />
            </div>
            <div class="meta-field">
              <label for="nro_nota_remision" class="form-label">N° de Nota</label>
              <input
                id="nro_nota_remision"
                type="text"
                ref="nroNotaInput"
                v-model="notaData.nro_nota_remision"
                class="form-control"
                placeholder="Número de nota de remisión"
                :readonly="readOnly"
                @keydown.enter.prevent="focusProviderInput()"
              />
            </div>
          </div>
        </div>
      </header>

      <form class="note-form" @submit.prevent="guardarNotaRemision">
        <section class="note-section">
          <h3 class="section-heading">Datos del proveedor</h3>
          <div class="section-grid">
            <div class="form-field">
              <label class="form-label">RUC / Proveedor</label>
              <div class="input-group">
                <ProviderSelect
                  ref="providerSelect"
                  v-model="selectedProviderInput"
                  :disabled="readOnly"
                  :bare="true"
                  :noList="true"
                  placeholder="RUC / Nombre del proveedor"
                  @provider-selected="onProviderSelected"
                  @register="abrirAltaProveedorDesdeBusqueda"
                  @input-enter="focusCodigoBarrasInput"
                />
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm px-3"
                  @click="toggleBuscarProveedor"
                  :disabled="readOnly"
                  title="Buscar proveedor"
                >
                  Buscar
                </button>
              </div>
            </div>
            <div class="form-field">
              <label for="nombre_razon_social" class="form-label">Razón Social</label>
              <input
                id="nombre_razon_social"
                type="text"
                ref="razonSocialInput"
                v-model="notaData.nombre_razon_social"
                class="form-control"
                placeholder="Nombre o razón social"
                :readonly="readOnly"
                @keydown.enter.prevent="focusCodigoBarrasInput()"
              />
            </div>
          </div>
        </section>

        <section class="note-section">
          <div class="section-heading with-line">Detalle de mercaderías</div>
          <div class="detail-entry">
            <div class="detail-field code">
              <label class="form-label">Código / SKU / Presentación</label>
              <div class="input-group code-input-group">
                <input
                  ref="codigoBarrasInput"
                  type="text"
                  v-model="productoData.codigo_barras"
                  class="form-control"
                  placeholder="Producto o presentación"
                  @blur="autocompletarProducto"
                  @keydown.enter.prevent="autocompletarProducto"
                  :readonly="readOnly"
                />
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm px-3"
                  @click="toggleBuscarProducto"
                  :disabled="readOnly"
                  title="Buscar producto"
                >
                  Buscar
                </button>
              </div>
            </div>
            <div class="detail-field qty">
              <label class="form-label">Cantidad</label>
              <input
                type="number"
                ref="cantidadInput"
                v-model="productoData.cantidad"
                class="form-control"
                placeholder="Cantidad"
                min="0"
                step="any"
                :readonly="readOnly"
                @keydown.enter.prevent="focusRef('fechaVencimientoInput')"
              />
            </div>
            <div class="detail-field unit">
              <label class="form-label">Unidad</label>
              <input
                type="text"
                v-model="productoData.unidad_medida"
                class="form-control"
                placeholder="Unidad de medida"
                readonly
              />
            </div>
            <div class="detail-field description">
              <label class="form-label">Descripción</label>
              <input
                type="text"
                v-model="productoData.descripcion"
                class="form-control"
                placeholder="Descripción de la mercadería"
                readonly
              />
              <!-- SPEC_PRESENTACIONES_EN_COMPRAS §12 RF-1/RF-2/RF-4 (D1). -->
              <SelectorPresentacion
                v-if="productoData.id && !readOnly && productoEditandoIndex === null"
                :model-value="productoData.presentacion"
                :producto-id="productoData.id"
                :producto-nombre="productoData.descripcion"
                testid="remision-selector-presentacion"
                @update:model-value="usarPresentacionEnLinea(productoData, $event)"
              />
            </div>
            <div class="detail-field due-date">
              <label class="form-label">Fecha de Vencimiento</label>
              <input
                v-if="!readOnly"
                type="text"
                ref="fechaVencimientoInput"
                class="form-control"
                :placeholder="DATE_PLACEHOLDER"
                inputmode="numeric"
                maxlength="10"
                @input="onFechaVencimientoTyping"
                @keydown.enter.prevent="agregarProductoDesdeTeclado"
              />
              <input
                v-else
                type="text"
                :value="formatDateDisplay(productoData.fechaVencimiento)"
                class="form-control"
                readonly
              />
            </div>
          </div>

          <div v-if="productoData.presentacion" class="presentation-preview" data-testid="remision-conversion">
            <div>{{ textoConversion(productoData.cantidad, null, productoData.presentacion) }}</div>
            <small v-if="avisoCantidad(productoData.cantidad, productoData.presentacion)" class="text-danger d-block" data-testid="remision-aviso-unidades">
              {{ avisoCantidad(productoData.cantidad, productoData.presentacion) }}
            </small>
          </div>
          <div v-else-if="isPresentationLine(productoData)" class="presentation-preview">
            <div>
              <strong>{{ productoData.presentacion_nombre }}</strong>
              <span>{{ productoData.tipo_presentacion }}</span>
            </div>
            <ul>
              <li v-for="component in convertedComponents(productoData)" :key="component.producto_id">
                {{ component.producto_nombre }}:
                {{ formatQuantity(component.cantidad_convertida) }}
                {{ component.unidad_medida_nombre || 'unidad' }}
              </li>
            </ul>
          </div>

          <div class="detail-actions" v-if="!readOnly">
            <button
              type="button"
              class="btn btn-secondary"
              @click="agregarProducto"
              :disabled="readOnly || !canAddProduct"
            >
              Agregar Producto
            </button>
          </div>

          <SimpleRegisterModal
            v-model:showModal="showRegisterModal"
            :title="registerModalTitle"
            @close="showRegisterModal = false"
            @register="irARegistro"
          />

          <h4 class="section-subheading">Productos agregados</h4>
          <AppTable
            :headers="['Origen', 'Código', 'Cantidad', 'Unidad operativa', 'Descripción', 'Acciones']"
            tableClass="table table-bordered table-sm note-details-table"
          >
            <tr
              v-for="(producto, index) in productos"
              :key="index"
              :class="{ 'fila-rechazada': lineaRechazada === index + 1 }"
              :data-testid="`remision-fila-${index + 1}`"
              :data-rechazada="lineaRechazada === index + 1 ? 'si' : null"
            >
              <td>
                <span :class="['source-pill', isPresentationLine(producto) ? 'presentation' : 'product']">
                  {{ isPresentationLine(producto) ? 'Presentación' : 'Producto' }}
                </span>
              </td>
              <td><span>{{ producto.codigo_barras }}</span></td>
              <td class="col-numeric">
                <input
                  v-if="productoEditandoIndex === index"
                  v-model.number="productoData.cantidad"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                />
                <span v-else>{{ producto.cantidad }}</span>
              </td>
              <td><span>{{ producto.unidad_medida }}</span></td>
              <td>
                <span>{{ producto.descripcion }}</span>
                <SelectorPresentacion
                  v-if="productoEditandoIndex === index && productoData.id"
                  :model-value="productoData.presentacion"
                  :producto-id="productoData.id"
                  :producto-nombre="productoData.descripcion"
                  :testid="`remision-fila-presentacion-${index + 1}`"
                  @update:model-value="usarPresentacionEnLinea(productoData, $event)"
                />
                <small v-if="producto.presentacion" class="d-block text-info-emphasis">
                  {{ textoConversion(producto.cantidad, null, producto.presentacion) }}
                </small>
                <small
                  v-if="productoEditandoIndex === index && avisoCantidad(productoData.cantidad, productoData.presentacion)"
                  class="d-block text-danger"
                >
                  {{ avisoCantidad(productoData.cantidad, productoData.presentacion) }}
                </small>
                <div v-else-if="isPresentationLine(producto) && !producto.presentacion" class="converted-summary">
                  <span v-for="component in convertedComponents(producto)" :key="component.producto_id">
                    {{ component.producto_nombre }}:
                    {{ formatQuantity(component.cantidad_convertida) }}
                    {{ component.unidad_medida_nombre || 'unidad' }}
                  </span>
                </div>
              </td>
              <td class="actions-col">
                <template v-if="productoEditandoIndex === index">
                  <button
                    type="button"
                    class="btn btn-success btn-sm me-1"
                    @click="guardarEdicionProducto"
                    :disabled="readOnly"
                  >
                    Guardar
                  </button>
                  <button
                    type="button"
                    class="btn btn-warning btn-sm me-1"
                    @click="cancelarEdicion"
                    :disabled="readOnly"
                  >
                    Cancelar
                  </button>
                </template>
                <template v-else>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm me-1"
                    @click="editarProducto(index)"
                    :disabled="readOnly"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    class="btn btn-danger btn-sm"
                    @click="eliminarProducto(index)"
                    :disabled="readOnly"
                  >
                    Eliminar
                  </button>
                </template>
              </td>
            </tr>
          </AppTable>
        </section>

        <div class="submit-row" v-if="!readOnly">
          <div v-if="atajosHints.length" class="shortcuts-hint d-none d-lg-flex">
            <span v-for="hint in atajosHints" :key="hint.key" class="shortcut-hint-item">
              <kbd>{{ hint.key }}</kbd> {{ hint.label }}
            </span>
          </div>
          <div class="submit-actions">
            <button
              type="button"
              class="btn btn-danger btn-lg"
              @click="cancelarNota"
            >
              Cancelar <kbd class="ms-1 d-none d-lg-inline">F8</kbd>
            </button>
            <button
              type="submit"
              class="btn btn-success btn-lg"
            >
              Guardar Nota de Remisión <kbd class="ms-1 d-none d-lg-inline">F9</kbd>
            </button>
          </div>
        </div>
      </form>

      <RegisterProductModal
        v-model:showModal="showProductModal"
        :initial-code="initialProductCode"
        @product-registered="onProductRegistered"
        @close-all-register-modals="showRegisterModal = false"
      />

      <RegistrarProveedorModal
        v-model:showModal="showProviderModal"
        :initial-ruc="initialProviderDocument"
        :initial-nombre="initialProviderName"
        @provider-registered="onProviderRegistered"
      />

  <div v-if="mostrarSelectorProveedor" class="overlay-backdrop">
        <div class="overlay-panel">
          <div class="overlay-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Seleccionar Proveedor</h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrarSelectorProveedor"></button>
          </div>
          <div class="overlay-body">
            <ProveedoresView
              :selectorMode="true"
              :hideHeader="false"
              :allowRegisterInSelector="true"
              @proveedor-seleccionado="onProveedorSeleccionado"
            />
          </div>
        </div>
      </div>
  <div v-if="mostrarSelectorProducto" class="overlay-backdrop">
          <div class="overlay-panel card">
            <div class="overlay-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Seleccionar Producto</h5>
              <button type="button" class="btn-close" @click="cerrarSelectorProducto"></button>
            </div>
            <div class="overlay-body">
              <ProductSelector presentation-purpose="compra" :allow-create="true" @product-selected="onProductoSeleccionado" @create-product="abrirAltaDesdeSelector" />
            </div>
          </div>
        </div>
    </div>
  </div>
</template>
  
<script>

import AppNavbar from '@/components/AppNavbar.vue';
import AppTable from '@/components/AppTable.vue';
import apiService from '@/services/apiService.js';
import SimpleRegisterModal from '@/components/SimpleRegisterModal.vue';
import ProviderSelect from '@/components/ProviderSelect.vue';
import RegistrarProveedorModal from '@/components/RegistrarProveedorModal.vue';
import RegisterProductModal from '@/components/RegistrarProductoModal.vue';
import ProveedoresView from '@/views/ProveedoresView.vue';
import ProductSelector from '@/components/ProductSelector.vue';
import productPresentationsService from '@/services/productPresentationsService';
import SelectorPresentacion from '@/components/SelectorPresentacion.vue';
import { presentacionDeCompra, textoConversion, avisoCantidad, lineaDelRechazo } from '@/utils/presentacionCompra.mjs';
import keyboardShortcutService from '@/services/keyboardShortcutService';
import salidaProtegidaService from '@/services/salidaProtegidaService';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { Spanish } from 'flatpickr/dist/l10n/es.js';

export default {
  name: 'NotaDeRemision',
  components: {
  AppNavbar,
    AppTable,
    SimpleRegisterModal,
    ProviderSelect,
    RegistrarProveedorModal,
    RegisterProductModal,
    ProveedoresView,
    ProductSelector,
    SelectorPresentacion
  },
  props: ['id'], // Recibe el id como prop
  data() {
    return {
      DATE_PICKER_FORMAT: 'd/m/Y',
      DATE_PLACEHOLDER: 'dd/mm/yyyy',
      fechaEmisionPicker: null,
      fechaVencimientoPicker: null,
      updatingFechaEmision: false,
      updatingFechaVencimiento: false,
      notaData: {
        nro_nota_remision: '',
        timbrado: '',
        // Guardamos en formato "YYYY-MM-DD" para que el input type=date funcione.
        fecha_emision: '',
        tipo_moneda: 'PYG',
        condicionVenta: 'Contado', // se usará en "credito_contado"
        ruc: '',
        nombre_razon_social: '',
        direccion: '',
        // Una nota nueva siempre nace pendiente de facturar: todavía no
        // existe ninguna compra/factura asociada. `false` acá hacía que el
        // backend confundiera "sin factura" con "ya facturada" (ver guard en
        // purchasesController.js) y bloqueara tanto "Generar Factura" como,
        // indirectamente, cualquier intento de reintentarlo.
        pendiente: true,

      },
      selectedProviderInput: '',
      showProductModal:false,
      initialProductCode: '',
      productoData: {
        id: null,            // ID del producto (si existe)
        codigo: '',          // Se puede usar para buscar el producto
        codigo_producto: '', // Código interno del producto
        codigo_barras: '',   // Código de barras
        descripcion: '',
        cantidad: 0,
        unidad_medida: '',
        iva: 10.00,          // Valor numérico del IVA
        fechaVencimiento: '',
        origen_linea: 'PRODUCTO',
        presentacion_id: null,
        presentacion_nombre: '',
        tipo_presentacion: null,
        componentes_presentacion: []
      },
      showRegisterModal: false,
      showProviderModal: false,
      mostrarSelectorProveedor: false,
      mostrarSelectorProducto: false,
      // RF-15: línea que el API rechazó (desde 1), para marcarla.
      lineaRechazada: null,
      registerModalTitle: '',
      nuevoProducto: {
        codigo: '',
        descripcion: '',
        cantidad: 0,
        unidadMedida: '',
        fechaVencimiento: '',
      },
      readOnly: false,
      productos: [],
      notasDeRemision: [],
      productoEditandoIndex: null,
      originalProducto: null,
      initialProviderDocument: '',
      initialProviderName: '',
      skipProviderLookupOnce: false,
      unregisterShortcuts: null,
      shortcutsReady: false
    };
  },
  watch: {
    'notaData.fecha_emision'(newVal) {
      if (this.updatingFechaEmision) return;
      this.updatePickerDate(this.fechaEmisionPicker, newVal);
    },
    'productoData.fechaVencimiento'(newVal) {
      if (this.updatingFechaVencimiento) return;
      this.updatePickerDate(this.fechaVencimientoPicker, newVal);
    }
  },
  computed: {
    // Un atajo de pantalla no debe dispararse con un overlay o un modal
    // arriba: ahí manda el diálogo, no la pantalla que quedó atrás
    // (§3.3 del SPEC).
    hayOverlayAbierto() {
      return Boolean(
        this.mostrarSelectorProveedor ||
        this.mostrarSelectorProducto ||
        this.showRegisterModal ||
        this.showProductModal ||
        this.showProviderModal
      );
    },
    atajosHints() {
      // `shortcutsReady` no es decorativo: `getActiveBindings` lee un array
      // interno del servicio que no es reactivo, y en el primer render el
      // scope todavía no está registrado (`mounted` no corrió). Sin una
      // dependencia reactiva que se invalide al registrar, este computed queda
      // cacheado en `[]` y la barra de hints nunca aparece.
      if (!this.shortcutsReady) return [];
      return keyboardShortcutService.getActiveBindings('nota-de-remision');
    },
    // ¿Hay algo que perder si el operador cancela? Si la nota está vacía no
    // tiene sentido pedirle que confirme nada.
    tieneDatosCargados() {
      return Boolean(
        (this.productos && this.productos.length) ||
        String(this.notaData.nro_nota_remision || '').trim() ||
        String(this.notaData.timbrado || '').trim() ||
        String(this.selectedProviderInput || '').trim() ||
        String(this.notaData.nombre_razon_social || '').trim() ||
        String(this.productoData.codigo_barras || '').trim()
      );
    },
    canAddProduct() {
      const cantidad = Number(this.productoData.cantidad);
      return Boolean(
        String(this.productoData.codigo_barras || '').trim() &&
        String(this.productoData.descripcion || '').trim() &&
        String(this.productoData.unidad_medida || '').trim() &&
        Number.isFinite(cantidad) &&
        cantidad > 0 &&
        // RF-12 (H-4): por caja, la cantidad tiene que dar unidades enteras.
        !avisoCantidad(cantidad, this.productoData.presentacion)
      );
    }
  },
  methods: {
    notifyError(message, title = 'Error') {
      window.alert.error(message, { title });
    },
    isPresentationLine(producto = {}) {
      return Boolean(producto.presentacion_id || producto.origen_linea === 'PRESENTACION');
    },
    formatQuantity(value) {
      const number = Number(value) || 0;
      return Number.isInteger(number) ? String(number) : number.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
    },
    convertedComponents(producto = {}) {
      const cantidad = Number(producto.cantidad) || 0;
      return (producto.componentes_presentacion || []).map((component) => ({
        ...component,
        cantidad_convertida: cantidad * Number(component.cantidad_operativa || 0)
      }));
    },
    componentsLabel(presentation = {}) {
      const components = Array.isArray(presentation.componentes) ? presentation.componentes : [];
      if (!components.length) return 'Sin componentes';
      return components
        .map((component) => `${this.formatQuantity(component.cantidad_operativa)} ${component.unidad_medida_nombre || ''} ${component.producto_nombre || ''}`.trim())
        .join(' + ');
    },
    async findPurchasePresentation(identifier) {
      const search = String(identifier || '').trim();
      if (!search) return null;
      const presentations = await productPresentationsService.list({ search, includeInactive: false });
      const normalized = search.toLowerCase();
      const match = (presentations || []).find((presentation) => {
        if (!presentation.es_presentacion_compra) return false;
        return [presentation.codigo_barras, presentation.sku_presentacion, presentation.nombre]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase() === normalized);
      }) || null;
      return match ? this.loadFullPresentation(match) : null;
    },
    async loadFullPresentation(presentation) {
      if (!presentation?.presentacion_id) return presentation;
      const full = await productPresentationsService.get(presentation.presentacion_id);
      return full || presentation;
    },
    // Código de barras o SKU de una caja: completa el producto base y la
    // presentación juntos (SPEC_PRESENTACIONES_EN_COMPRAS §12 RF-2).
    applyPresentation(full) {
      const presentacion = presentacionDeCompra(full);
      if (presentacion.error) {
        this.notifyError(presentacion.error, 'Presentación');
        return;
      }
      const [componente] = full.componentes || [];
      this.productoData = {
        ...this.productoData,
        id: presentacion.producto_id,
        codigo: full.sku_presentacion || full.codigo_barras || '',
        codigo_producto: full.sku_presentacion || '',
        codigo_barras: full.codigo_barras || full.sku_presentacion || full.nombre,
        descripcion: presentacion.producto_nombre || full.nombre,
        unidad_medida: componente?.unidad_medida_nombre || this.productoData.unidad_medida || 'unidad'
      };
      this.usarPresentacionEnLinea(this.productoData, presentacion);
      this.focusCantidadInput();
    },
    // RF-2: Unidad (null) o caja en una línea.
    usarPresentacionEnLinea(linea, presentacion) {
      linea.presentacion = presentacion || null;
      linea.presentacion_id = presentacion?.presentacion_id || null;
      linea.presentacion_nombre = presentacion?.nombre || '';
      linea.tipo_presentacion = presentacion ? 'CONVERSION' : null;
      linea.origen_linea = presentacion ? 'PRESENTACION' : 'PRODUCTO';
      linea.componentes_presentacion = [];
    },
    textoConversion,
    avisoCantidad,
    notifySuccess(message, title = 'Operación exitosa') {
      window.alert.success(message, { title });
    },
    resolveErrorMessage(error, fallback) {
      return error?.userMessage
        || error?.response?.data?.details
        || error?.response?.data?.message
        || fallback;
    },
    // Foco genérico por ref. `Enter` en esta pantalla significa "avanzar al
    // siguiente campo del flujo real de carga", no "enviar el formulario"
    // (FE-UXO-001): el envío implícito del navegador es justamente lo que
    // guardaba la nota entera a mitad de carga.
    focusRef(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        const target = Array.isArray(el) ? el[0] : el;
        if (target && typeof target.focus === 'function') {
          target.focus();
          target.select?.();
        }
      });
    },
    focusProviderInput() {
      this.$nextTick(() => {
        const input = this.$refs.providerSelect?.$el?.querySelector?.('input')
          || this.$refs.providerSelect?.$el;
        if (input && typeof input.focus === 'function') {
          input.focus();
          input.select?.();
        }
      });
    },
    // `Enter` sobre Fecha de Vencimiento agrega la línea al detalle. Con la
    // línea incompleta no hace nada y no muestra error: es el mismo criterio
    // que el botón "Agregar Producto", que ya está deshabilitado en ese estado.
    agregarProductoDesdeTeclado() {
      if (this.readOnly || !this.canAddProduct) return;
      this.agregarProducto();
    },
    async cancelarNota() {
      if (this.readOnly) return;
      if (this.tieneDatosCargados) {
        const confirmado = await window.confirm(
          'Se perderá la nota de remisión en carga. ¿Confirmás?',
          { title: 'Cancelar nota de remisión', confirmText: 'Descartar', cancelText: 'Seguir cargando' }
        );
        if (!confirmado) return;
      }
      this.resetNota();
      this.limpiarCamposProducto();
      salidaProtegidaService.autorizarSalida();
      this.$router.back();
    },
    focusCantidadInput() {
      this.$nextTick(() => {
        const cantidadInput = this.$refs.cantidadInput;
        if (cantidadInput && typeof cantidadInput.focus === 'function') {
          cantidadInput.focus();
          cantidadInput.select?.();
        }
      });
    },
    focusCodigoBarrasInput() {
      this.$nextTick(() => {
        const codigoInput = this.$refs.codigoBarrasInput;
        if (codigoInput && typeof codigoInput.focus === 'function') {
          codigoInput.focus();
          codigoInput.select?.();
        }
      });
    },
    // Devuelve la fecha actual en formato "YYYY-MM-DD" (compatible con input type=date)
    todayISO() {
      const d = new Date();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    },
    // Convierte valores variados (ISO, YYYY-MM-DD, Date) a "dd/mm/yyyy" para display
    formatDateDisplay(value) {
      if (!value) return '';
      try {
        // Si viene como YYYY-MM-DD
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const [y, m, d] = value.split('-');
          return `${d}/${m}/${y}`;
        }
        // Si viene como ISO o Date parseable
        const date = new Date(value);
        if (isNaN(date.getTime())) return '';
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yy = date.getFullYear();
        return `${dd}/${mm}/${yy}`;
      } catch (_) {
        return '';
      }
    },
    ddmmyyyyToISO(value) {
      if (!value) return '';
      const trimmed = value.trim();
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return '';
      const [day, month, year] = trimmed.split('/');
      return `${year}-${month}-${day}`;
    },
    normalizeDateDigits(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 8);
    },
    applyDateMask(value) {
      const digits = this.normalizeDateDigits(value);
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    },
    isoStringToDate(iso) {
      if (!iso) return null;
      const match = /^\d{4}-\d{2}-\d{2}$/.exec(iso.trim());
      if (!match) {
        const parsed = new Date(iso);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      const [year, month, day] = iso.split('-').map(Number);
      if (!year || !month || !day) return null;
      return new Date(year, month - 1, day);
    },
    initFechaEmisionPicker() {
      const input = this.$refs.fechaEmisionInput;
      if (!input) return;
      this.fechaEmisionPicker = flatpickr(input, {
        dateFormat: this.DATE_PICKER_FORMAT,
        locale: Spanish,
        defaultDate: this.isoStringToDate(this.notaData.fecha_emision),
        allowInput: true,
        onValueUpdate: (_, dateStr) => this.handleFechaEmisionInput(dateStr),
        onClose: (_, dateStr, instance) => {
          if (!dateStr) {
            this.handleFechaEmisionInput(instance.input.value);
          }
        }
      });
      this.updatePickerDate(this.fechaEmisionPicker, this.notaData.fecha_emision);
    },
    initFechaVencimientoPicker() {
      const input = this.$refs.fechaVencimientoInput;
      if (!input) return;
      this.fechaVencimientoPicker = flatpickr(input, {
        dateFormat: this.DATE_PICKER_FORMAT,
        locale: Spanish,
        defaultDate: this.isoStringToDate(this.productoData.fechaVencimiento),
        allowInput: true,
        onValueUpdate: (_, dateStr) => this.handleFechaVencimientoInput(dateStr),
        onClose: (_, dateStr, instance) => {
          if (!dateStr) {
            this.handleFechaVencimientoInput(instance.input.value);
          }
        }
      });
      this.updatePickerDate(this.fechaVencimientoPicker, this.productoData.fechaVencimiento);
    },
    onFechaEmisionTyping(event) {
      const maskedValue = this.applyDateMask(event.target.value);
      event.target.value = maskedValue;
      this.handleFechaEmisionInput(maskedValue);
    },
    onFechaVencimientoTyping(event) {
      const maskedValue = this.applyDateMask(event.target.value);
      event.target.value = maskedValue;
      this.handleFechaVencimientoInput(maskedValue);
    },
    handleFechaEmisionInput(rawValue) {
      const trimmed = (rawValue || '').trim();
      if (!trimmed) {
        this.updatingFechaEmision = true;
        this.notaData.fecha_emision = '';
        this.$nextTick(() => {
          this.updatingFechaEmision = false;
        });
        return;
      }
      const iso = this.ddmmyyyyToISO(trimmed);
      if (!iso) return;
      this.updatingFechaEmision = true;
      this.notaData.fecha_emision = iso;
      this.$nextTick(() => {
        this.updatingFechaEmision = false;
      });
    },
    handleFechaVencimientoInput(rawValue) {
      const trimmed = (rawValue || '').trim();
      if (!trimmed) {
        this.updatingFechaVencimiento = true;
        this.productoData.fechaVencimiento = '';
        this.$nextTick(() => {
          this.updatingFechaVencimiento = false;
        });
        return;
      }
      const iso = this.ddmmyyyyToISO(trimmed);
      if (!iso) return;
      this.updatingFechaVencimiento = true;
      this.productoData.fechaVencimiento = iso;
      this.$nextTick(() => {
        this.updatingFechaVencimiento = false;
      });
    },
    updatePickerDate(picker, iso) {
      if (!picker) return;
      if (!iso) {
        picker.clear();
        return;
      }
      const dateObj = this.isoStringToDate(iso);
      if (!dateObj) {
        picker.clear();
        return;
      }
      const desired = this.formatDateDisplay(iso);
      if (picker.input.value !== desired) {
        picker.setDate(dateObj, false);
      }
    },
    async autocompletarProducto() {
      if (!this.productoData.codigo_barras) return;
      try {
        const presentation = await this.findPurchasePresentation(this.productoData.codigo_barras);
        if (presentation) {
          this.applyPresentation(presentation);
          return;
        }
        const url = `${process.env.VUE_APP_API_BASE_URL}/api/products/barcode/${this.productoData.codigo_barras}`;
        const response = await apiService.get(url);
        const producto = response.data;
        if (producto) {
          this.productoData.descripcion = producto.nombre;
          this.productoData.unidad_medida = producto.unidad_medida_nombre;
          this.productoData.fechaVencimiento = producto.fecha_vencimiento;
          this.productoData.id = producto.producto_id || null;
          this.usarPresentacionEnLinea(this.productoData, null);
          this.focusCantidadInput();
        } else {
          this.nuevoProducto.codigo = this.productoData.codigo_barras;
          this.initialProductCode = this.productoData.codigo_barras;
          this.registerModalTitle = '';
          this.showRegisterModal = false;
          this.showProductModal = true;
        }
      } catch (error) {
        console.error("Error al obtener el producto:", error);
        this.nuevoProducto.codigo = this.productoData.codigo_barras;
        this.initialProductCode = this.productoData.codigo_barras;
        this.registerModalTitle = '';
        this.showRegisterModal = false;
        this.showProductModal = true;
      }
    },
    onProductRegistered(newProduct) {
      this.productoData.codigo_barras = newProduct.codigo_barras || newProduct.codigo;
      this.productoData.descripcion = newProduct.descripcion;
      this.productoData.unidad_medida = newProduct.unidad_medida || newProduct.unidadMedida;
      this.productoData.fechaVencimiento = newProduct.fechaVencimiento;
      this.showProductModal = false;
      this.autocompletarProducto();
      // FE-UXO-003: el modal se lleva el foco al abrirse; al cerrarse hay que
      // devolverlo o el operador queda sin cursor en ningún campo.
      this.focusCantidadInput();
    },
    closeRegisterModal() {
      this.showRegisterModal = false;
      this.nuevoProducto = {
        codigo: '',
        descripcion: '',
        cantidad: 0,
        unidadMedida: '',
        fechaVencimiento: '',
      };
    },
    toggleBuscarProveedor() {
      if (this.readOnly) return;
      this.mostrarSelectorProveedor = !this.mostrarSelectorProveedor;
      document.body.style.overflow = this.mostrarSelectorProveedor ? 'hidden' : '';
      if (this.mostrarSelectorProveedor) {
        this.$nextTick(() => {
          const firstInput = document.querySelector('.overlay-panel input');
          if (firstInput) firstInput.focus();
        });
      }
    },
    // El alta de proveedor ya no se abre sola. Antes la disparaba tanto el 404
    // del lookup exacto como el `catch` genérico, así que un error de red o un
    // 500 se convertían en un formulario de carga. Ahora la búsqueda vive en
    // `ProviderSelect` y el alta requiere una acción explícita del operador
    // (RF-3 del SPEC).
    abrirAltaProveedorDesdeBusqueda(payload = {}) {
      if (this.readOnly) return;
      this.initialProviderDocument = payload.documento || '';
      this.initialProviderName = payload.nombre || '';
      this.showProviderModal = true;
    },
    abrirModalRegistroProveedor(documento = '', nombre = '') {
      this.initialProviderDocument = documento;
      this.initialProviderName = nombre;
      this.showProviderModal = true;
    },
    openProviderModal() {
      if (this.readOnly) return;
      const texto = (this.selectedProviderInput || '').trim();
      const esDocumento = /^[0-9-]+$/.test(texto);
      this.abrirModalRegistroProveedor(esDocumento ? texto : '', esDocumento ? '' : texto);
    },
    cerrarSelectorProveedor() {
      this.mostrarSelectorProveedor = false;
      document.body.style.overflow = '';
      // Sin esto el foco queda huérfano en el body y el operador tiene que
      // volver al lector con el mouse (FE-UXO-003).
      this.focusCodigoBarrasInput();
    },
    onProveedorSeleccionado(prov) {
      this.onProviderSelected(prov);
      this.cerrarSelectorProveedor();
    },
    toggleBuscarProducto() {
      if (this.readOnly) return;
      this.mostrarSelectorProducto = !this.mostrarSelectorProducto;
      document.body.style.overflow = this.mostrarSelectorProducto ? 'hidden' : '';
    },
    cerrarSelectorProducto() {
      this.mostrarSelectorProducto = false;
      document.body.style.overflow = '';
      // El producto ya quedó resuelto: lo que sigue es la cantidad.
      this.focusCantidadInput();
    },
    async onProductoSeleccionado(producto) {
      if (!producto) return;

      if (producto.type === 'PRESENTACION' || producto.presentacion_id) {
        const fullPresentation = await this.loadFullPresentation(producto);
        this.applyPresentation(fullPresentation);
        this.cerrarSelectorProducto();
        return;
      }

      const codigo = producto.code || producto.codigo_barras || producto.codigo_producto || producto.codigo || producto.barcode || '';

      this.productoData.id = producto.producto_id || producto.id || null;
      this.productoData.codigo_producto = producto.codigo_producto || codigo;
      this.productoData.codigo_barras = codigo;
      this.productoData.descripcion = producto.name || producto.nombre || producto.descripcion || this.productoData.descripcion;
      this.productoData.unidad_medida = producto.unit || producto.unidad_medida_nombre || producto.unidad_medida || producto.unidad || this.productoData.unidad_medida;
      this.usarPresentacionEnLinea(this.productoData, null);
      if (producto.iva) {
        this.productoData.iva = producto.iva;
      }

      this.cerrarSelectorProducto();
      this.focusCantidadInput();
    },
    cancelarEdicion() {
      // Si quieres revertir el producto en el array:
      if (this.productoEditandoIndex !== null && this.originalProducto) {
        this.productos[this.productoEditandoIndex] = { ...this.originalProducto };
      }

      // Deja de editar y limpia campos
      this.productoEditandoIndex = null;
      this.limpiarCamposProducto();
      this.originalProducto = null;  // ya no se necesita la copia
    },
    registrarProducto() {
      //NotaDeRemisionService.guardarProducto(this.nuevoProducto); // Implementa esta función en el servicio mock
      //this.productoData = { ...this.nuevoProducto }; // Copiar datos del nuevo producto al formulario principal
      this.closeRegisterModal(); // Cerrar el modal
    },
    openProductModal(){
      this.showProductModal = true;
    },
    // compras-carga-productos-spec.md RF-1: alta manual desde «Seleccionar
    // Producto», sin código precargado (el producto puede no tener código de
    // barras). Al registrarse, `onProductRegistered` lo carga en la línea.
    abrirAltaDesdeSelector() {
      if (this.readOnly) return;
      this.cerrarSelectorProducto();
      this.initialProductCode = '';
      this.registerModalTitle = '';
      this.showRegisterModal = false;
      this.showProductModal = true;
    },
    async cargarNotaDeRemision(id) {
      try {
        const { data: nota } = await apiService.get(`${process.env.VUE_APP_API_BASE_URL}/api/purchases/delivery-notes/${id}`);
        if (nota) {
          const cabecera = nota.cabecera;

          this.selectedProviderInput = cabecera.nro_documento.toString();
          this.onProviderSelected({
            nro_documento: cabecera.nro_documento.toString(),
            nombre: cabecera.nombre_razon_social
          });

          this.notaData = {
            nro_nota_remision: cabecera.nro_nota_remision,
            timbrado: cabecera.timbrado,
            // Guardar como YYYY-MM-DD para el input date cuando sea editable.
            fecha_emision: cabecera.fecha_emision ? cabecera.fecha_emision.split('T')[0] : '',
            tipo_moneda: cabecera.tipo_moneda,
            condicionVenta: cabecera.credito_contado,
            nro_documento: cabecera.nro_documento,
            nombre_razon_social: cabecera.nombre_razon_social,
            direccion: cabecera.direccion,
            pendiente: cabecera.pendiente
          };
          this.productos = nota.detalles.map(detalle => ({
            producto_id: detalle.producto_id,
            cantidad: detalle.cantidad,
            unidad_medida: detalle.unidad_medida,
            codigo_producto: detalle.codigo_producto,
            codigo_barras: detalle.codigo_barras,
            descripcion: detalle.nombre_producto,
            iva: detalle.iva,
            fechaVencimiento: detalle.fecha_vencimiento,
            origen_linea: detalle.presentacion_id ? 'PRESENTACION' : 'PRODUCTO',
            presentacion_id: detalle.presentacion_id || null,
            presentacion_nombre: detalle.presentacion_nombre || '',
            componentes_presentacion: []
          }));
        }
      } catch (error) {
        console.error('Error al cargar la nota de remisión:', error);
        this.notifyError(this.resolveErrorMessage(error, 'No se pudo cargar la nota de remisión.'));
      }
    },
    async guardarNotaRemision() {
      this.lineaRechazada = null;
      try {
        if (!Array.isArray(this.productos) || this.productos.length === 0) {
          window.alert.warning('Debe agregar al menos un producto antes de guardar la nota de remisión', {
            title: 'Datos incompletos'
          });
          return;
        }
        const requestBody = {
          cabecera: {
            nro_nota_remision: this.notaData.nro_nota_remision,
            timbrado: this.notaData.timbrado,
            fecha_emision: this.notaData.fecha_emision ? new Date(this.notaData.fecha_emision).toISOString() : null,
            tipo_moneda: this.notaData.tipo_moneda || 'PYG',
            credito_contado: this.notaData.condicionVenta,
            tipo_documento: 'RUC',
            nro_documento: this.selectedProviderInput,
            nombre_razon_social: this.notaData.nombre_razon_social,  // O ajustar según convenga
            direccion: this.notaData.direccion,
            pendiente: this.notaData.pendiente
          },
          detalles: this.productos.map(producto => ({
            producto_id: producto.presentacion_id ? undefined : (producto.id || producto.producto_id || producto.codigo_barras),
            presentacion_id: producto.presentacion?.presentacion_id || producto.presentacion_id || undefined,
            cantidad: Number(producto.cantidad),
            cantidad_presentacion_original: producto.presentacion_id ? Number(producto.cantidad) : undefined,
            unidad_medida: producto.unidad_medida,
            codigo_producto: producto.codigo_producto || producto.codigo || '',
            codigo_barras: producto.codigo_barras || producto.codigo || '',
            descripcion: producto.descripcion,
            iva: Number(producto.iva),
            fecha_vencimiento: producto.fechaVencimiento ? new Date(producto.fechaVencimiento).toISOString().split('T')[0] : null
          }))
        };
        console.log("Request Body:", requestBody);
        // Como apiService ya tiene configurada la URL base, usamos el endpoint relativo
        const url = `${process.env.VUE_APP_API_BASE_URL}/api/purchases/delivery-notes`;
        const response = await apiService.post(url, requestBody);
        this.notasDeRemision = response.data;
        this.notifySuccess('Nota de remisión guardada correctamente.');
        this.resetNota();
        salidaProtegidaService.autorizarSalida();
        this.$router.back();
      } catch (error) {
        console.error('Error al guardar la nota de remisión:', error);
        // RF-15: la nota queda como estaba y editable; se marca la línea.
        const rechazo = lineaDelRechazo(error, this.resolveErrorMessage(error, 'No se pudo guardar la nota de remisión.'));
        this.lineaRechazada = rechazo.linea;
        this.notifyError(rechazo.linea ? `Línea ${rechazo.linea}: ${rechazo.mensaje}` : rechazo.mensaje);
      }
    },
    agregarProducto() {
      if (!this.canAddProduct) {
        return;
      }

      if (this.productoEditandoIndex !== null) {
        this.productos.splice(this.productoEditandoIndex, 1, { ...this.productoData });
        this.productoEditandoIndex = null;
      } else {
        this.productos.push({ ...this.productoData });
      }
      this.limpiarCamposProducto();
      this.focusCodigoBarrasInput();
    },
    irARegistro() {
      if (this.registerModalTitle === "Producto no encontrado") {
        // Navega a la página de registro de producto
        this.initialProductCode = this.productoData.codigo_barras;
        this.showProductModal   = true;
        console.log('[NotaDeRemision] initialProductCode →', this.initialProductCode);

      } else if (this.registerModalTitle === "Proveedor no encontrado") {
        this.showProviderModal = true  
      }
    },
    onProviderSelected(prov) {
      if (!prov) return;
      const documento = `${prov.nro_documento || ''}`.trim();
      this.skipProviderLookupOnce = true;
      this.selectedProviderInput = documento;
      this.notaData.nro_documento = documento;
      this.notaData.nombre_razon_social = prov.nombre || prov.nombre_razon_social || '';
      if (prov.direccion) {
        this.notaData.direccion = prov.direccion;
      }
      this.initialProviderDocument = documento;
      this.initialProviderName = '';
      this.focusCodigoBarrasInput();
    },
    editarProducto(index) {

      this.originalProducto = { ...this.productos[index] };

      this.productoData = { ...this.productos[index] };
      this.productoEditandoIndex = index;
    },
    guardarEdicionProducto() {
      this.agregarProducto();
    },
    eliminarProducto(index) {
      this.productos.splice(index, 1);
      this.focusCodigoBarrasInput();
    },
    limpiarCamposProducto() {
      this.productoData = {
        id: null,
        codigo: '',
        codigo_producto: '',
        codigo_barras: '',
        descripcion: '',
        cantidad: 0,
        unidad_medida: '',
        iva: 10.00,
        fechaVencimiento: '',
        origen_linea: 'PRODUCTO',
        presentacion_id: null,
        presentacion_nombre: '',
        tipo_presentacion: null,
        componentes_presentacion: [],
        presentacion: null
      };
      this.productoEditandoIndex = null;
    },
    onProviderRegistered(nuevoProv) {
      this.showProviderModal = false;
      if (nuevoProv) {
        this.onProviderSelected(nuevoProv);
      }
      this.$nextTick(() => {
        if (this.$refs.providerSelect && typeof this.$refs.providerSelect.loadProviders === 'function') {
          this.$refs.providerSelect.loadProviders();
        }
      });
    },
    resetNota() {
      this.notaData = {
      nro_nota_remision: '',
      timbrado: '',
      fecha_emision: this.todayISO(),
      tipo_moneda: 'PYG',
      condicionVenta: 'Contado',
      nro_documento: '',
      nombre_razon_social: '',
      direccion: '',
      pendiente: true
    };
    this.selectedProviderInput = '';
    this.initialProviderDocument = '';
    this.productos = [];
    }
  },
  async mounted() {
    // ux-operativa-v2-spec.md RF-3: recargar o salir con la nota en carga pide confirmación.
    this.desprotegerSalida = salidaProtegidaService.proteger({
      hayCambios: () => !this.readOnly && this.tieneDatosCargados
    });
    // Si hay un id, carga los datos de la nota de remisión específica
    if (this.id) {
      this.readOnly = true;
      await this.cargarNotaDeRemision(this.id);
    } else {
      // Nueva nota: fecha de emisión hoy por defecto
      this.notaData.fecha_emision = this.todayISO();
    }

    if (!this.readOnly) {
      this.$nextTick(() => {
        this.initFechaEmisionPicker();
        this.initFechaVencimientoPicker();
        this.focusCodigoBarrasInput();
      });

      // Mismo mapa de teclas que `PantallaInicio` (F9 confirmar/guardar, F8
      // cancelar): el operador aprende un solo par para todo el sistema.
      // `Enter` NO se registra acá a propósito — depende del campo con foco y
      // se maneja en el template (FE-UXO-001).
      this.unregisterShortcuts = keyboardShortcutService.register({
        scopeId: 'nota-de-remision',
        isActive: () => !this.hayOverlayAbierto,
        bindings: [
          { key: 'F9', run: () => this.guardarNotaRemision(), label: 'Guardar nota', enabled: () => !this.readOnly },
          { key: 'F8', run: () => this.cancelarNota(), label: 'Cancelar', enabled: () => !this.readOnly }
        ]
      });
      this.shortcutsReady = true;
    }
  },
  beforeUnmount() {
    if (this.desprotegerSalida) this.desprotegerSalida();
    if (this.unregisterShortcuts) {
      this.unregisterShortcuts();
      this.unregisterShortcuts = null;
    }
    if (this.fechaEmisionPicker) {
      this.fechaEmisionPicker.destroy();
      this.fechaEmisionPicker = null;
    }
    if (this.fechaVencimientoPicker) {
      this.fechaVencimientoPicker.destroy();
      this.fechaVencimientoPicker = null;
    }
  }
};
</script>


  
<style scoped>
/* RF-15: la línea que el API rechazó, para corregirla ahí. */
.fila-rechazada td { background: var(--bs-danger-bg-subtle, #f8d7da); }
.fila-rechazada td:first-child { box-shadow: inset 3px 0 0 var(--bs-danger, #dc3545); }
.note-wrapper {
  background-color: #f4f4f4;
  min-height: 100vh;
  padding: 32px 16px 48px;
}

.note-paper {
  max-width: 1120px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #d6d6d6;
  border-radius: 12px;
  box-shadow: 0 12px 35px rgba(15, 23, 42, 0.12);
  padding: 32px 36px 36px;
}

.note-header {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: space-between;
  align-items: stretch;
  padding-bottom: 12px;
  border-bottom: 3px double #747474;
  margin-bottom: 16px;
}

.note-header__brand {
  flex: 1;
  min-width: 260px;
}

.note-badge {
  display: inline-block;
  background: linear-gradient(135deg, #a5d6ff, #4fa0ff);
  color: #0f172a;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.75rem;
  padding: 6px 12px;
  border-radius: 999px;
  letter-spacing: 0.1em;
}

.note-title {
  /* ux-operativa-v2-spec.md RF-4 */
  font-size: 1.35rem;
  font-weight: 700;
  margin: 6px 0 4px;
  letter-spacing: 0.08em;
}

.note-description {
  color: #5f5f5f;
  max-width: 420px;
  margin: 0;
  line-height: 1.5;
}

.note-header__meta {
  background: linear-gradient(180deg, #f1f7ff 0%, #e2ecfe 100%);
  border: 1px solid #9dbdf7;
  border-radius: 12px;
  padding: 18px 20px;
  min-width: 280px;
  max-width: 340px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 14px 16px;
}

.meta-field label {
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #3d3d3d;
  margin-bottom: 4px;
  display: block;
}

.note-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.note-section {
  padding: 0;
}

.section-heading {
  font-size: 0.95rem;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #444;
  margin-bottom: 18px;
}

.section-heading.with-line {
  border-bottom: 2px solid #131313;
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.section-subheading {
  font-size: 0.88rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #4f4f4f;
  margin: 24px 0 12px;
}

.section-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 18px 24px;
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  padding: 18px;
  background: linear-gradient(180deg, #fafafa 0%, #fefefe 100%);
}

.form-field .input-group {
  display: flex;
  gap: 10px;
}

.form-field .btn {
  white-space: nowrap;
}

.detail-entry {
  display: grid;
  grid-template-columns:
    minmax(190px, 1.2fr)
    minmax(120px, 0.8fr)
    minmax(140px, 0.9fr)
    minmax(280px, 2fr)
    minmax(150px, 1fr);
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  background: #fdfdfd;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.detail-field label {
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #666;
  margin-bottom: 6px;
  display: block;
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.note-details-table {
  border-color: #8f8f8f !important;
}

.presentation-preview {
  margin-top: 0.8rem;
  padding: 0.85rem;
  border: 1px solid #bfdbfe;
  border-radius: 0.6rem;
  background: #eff6ff;
}

.presentation-preview > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.45rem;
}

.presentation-preview span,
.source-pill {
  display: inline-flex;
  align-items: center;
  min-height: 1.6rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.presentation-preview span,
.source-pill.presentation {
  background: #dbeafe;
  color: #1d4ed8;
}

.source-pill.product {
  background: #ecfdf5;
  color: #047857;
}

.presentation-preview ul {
  margin: 0;
  padding-left: 1.1rem;
  color: #1e3a8a;
}

.converted-summary {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-top: 0.35rem;
  color: #1d4ed8;
  font-size: 0.82rem;
}

.presentation-result-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.presentation-result {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 100%;
  padding: 0.75rem 0.85rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.55rem;
  background: #fff;
  text-align: left;
}

.presentation-result:hover {
  background: #f8fafc;
}

.presentation-result span,
.presentation-result small {
  color: #64748b;
}

.note-details-table thead th {
  background: #f2f2f2;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.note-details-table tbody td {
  vertical-align: middle;
  font-size: 0.9rem;
}

.col-numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.actions-col {
  width: 180px;
  text-align: center;
}

.submit-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  border-top: 2px solid #2b2b2b;
  padding-top: 24px;
}

.submit-actions {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.shortcuts-hint {
  align-items: center;
  gap: 14px;
  color: #495057;
  font-size: 0.8rem;
  white-space: nowrap;
}

.shortcuts-hint .shortcut-hint-item kbd {
  font-size: 0.7rem;
  padding: 1px 5px;
  margin-right: 2px;
}

.btn-success.btn-lg {
  min-width: 240px;
}

.overlay-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 40px;
  z-index: 1050;
}

.overlay-panel {
  width: min(1100px, 96%);
  max-height: 86vh;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.overlay-header {
  padding: 10px 14px;
  border-bottom: 1px solid #e5e7eb;
}

.overlay-body {
  padding: 4px 12px 12px;
  overflow: auto;
  max-height: calc(86vh - 48px);
}

@media (max-width: 992px) {
  .detail-entry {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .note-header__meta {
    max-width: 100%;
    width: 100%;
  }

  .submit-row {
    justify-content: center;
  }
}
</style>
  
